import { useEffect, useState } from 'react'
import { tournamentApi } from '../services/api'
import PortalShell from './setup/PortalShell'
import { useLanguage } from '../context/LanguageContext'

// Returns funnel-stage labels translated for the current language. Built as a
// function (not a static const) so labels re-render when the user switches
// languages.
function useFunnelLabels() {
  const { t } = useLanguage()
  return {
    package_picked: t('sp_funnel_package_picked', 'Picked package'),
    dates_selected: t('sp_funnel_dates_selected', 'Picked dates'),
    hotel_picked: t('sp_funnel_hotel_picked', 'Picked hotel'),
    payment_mode_selected: t('sp_funnel_payment_selected', 'Picked payment'),
    policies_accepted: t('sp_funnel_policies_accepted', 'Accepted policies'),
    booking_submitted: t('sp_funnel_booking_submitted', 'Submitted'),
    booking_succeeded: t('sp_funnel_booking_succeeded', 'Completed'),
  }
}

export default function Analytics() {
  const { t } = useLanguage()
  const [days, setDays] = useState(90)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    tournamentApi.get(`/play-stay/analytics?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [days])

  return (
    <PortalShell>
      <div style={{ maxWidth: 1100, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: 28, color: 'var(--secondary-950)', margin: 0 }}>{t('sp_analytics_title', 'Stay & Play analytics')}</h1>
          <select value={days} onChange={e => setDays(Number(e.target.value))}
            style={{ padding: '8px 12px', border: '1px solid var(--card-border)', borderRadius: 6, fontSize: 14 }}>
            <option value={7}>{t('sp_last_7_days', 'Last 7 days')}</option>
            <option value={30}>{t('sp_last_30_days', 'Last 30 days')}</option>
            <option value={90}>{t('sp_last_90_days', 'Last 90 days')}</option>
            <option value={180}>{t('sp_last_180_days', 'Last 180 days')}</option>
            <option value={365}>{t('sp_last_365_days', 'Last 365 days')}</option>
          </select>
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('sp_loading_analytics', 'Loading analytics…')}</div>}
        {error && <Card><div style={{ color: '#991b1b' }}>{error}</div></Card>}

        {data && (
          <>
            <KpiRow data={data} />
            <FunnelCard funnel={data.funnel} />
            <TopPackagesCard packages={data.topPackages} />
            <AffiliateCard affiliate={data.affiliate} />
            <HotelRefundCard hotel={data.hotelLatency} refunds={data.refunds} bookingKpis={data.bookingKpis} />
          </>
        )}
      </div>
    </PortalShell>
  )
}

function Card({ children, title, style }) {
  return (
    <div style={{ background: '#fff', padding: 20, borderRadius: 12, marginBottom: 16, border: '1px solid var(--card-border)', ...style }}>
      {title && <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--secondary-950)', marginBottom: 12 }}>{title}</div>}
      {children}
    </div>
  )
}

function KpiRow({ data }) {
  const { t } = useLanguage()
  const k = data.bookingKpis || {}
  const tiles = [
    { label: t('sp_kpi_bookings', 'Bookings'), value: Number(k.totalBookings) || 0 },
    { label: t('sp_kpi_active', 'Active'), value: Number(k.activeBookings) || 0, color: '#16a34a' },
    { label: t('sp_kpi_cancelled', 'Cancelled'), value: Number(k.cancelledBookings) || 0, color: '#dc2626' },
    { label: t('sp_kpi_gmv', 'GMV'), value: '$' + Number(k.gmv || 0).toFixed(2) },
    { label: t('sp_kpi_avg_trip', 'Avg trip'), value: '$' + Number(k.avgTripValue || 0).toFixed(2) },
    { label: t('sp_kpi_total_golfers', 'Total golfers'), value: Number(k.totalGolfers) || 0 },
    { label: t('sp_kpi_loyalty_given', 'Loyalty given'), value: '$' + Number(k.totalLoyaltyDiscountDollars || 0).toFixed(2) },
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
      {tiles.map((t, i) => (
        <div key={i} style={{ background: '#fff', padding: 14, borderRadius: 10, border: '1px solid var(--card-border)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: t.color || 'var(--secondary-950)', marginTop: 4 }}>{t.value}</div>
        </div>
      ))}
    </div>
  )
}

function FunnelCard({ funnel }) {
  const { t } = useLanguage()
  const FUNNEL_STAGE_LABELS = useFunnelLabels()
  if (!funnel || funnel.length === 0) return null
  const top = funnel[0]?.sessions || 0
  return (
    <Card title={t('sp_booking_funnel', 'Booking funnel')}>
      {top === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('sp_no_widget_activity', 'No widget activity yet in this window.')}</div>
      )}
      {funnel.map((f, i) => {
        const w = top > 0 ? (f.sessions / top) * 100 : 0
        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: 'var(--secondary-950)' }}>{FUNNEL_STAGE_LABELS[f.stage] || f.stage}</span>
              <span style={{ color: 'var(--text-muted)' }}>{f.sessions} ({f.conversionFromTop}%)</span>
            </div>
            <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: w + '%', height: '100%', background: '#00d19f', transition: 'width 0.3s' }} />
            </div>
          </div>
        )
      })}
    </Card>
  )
}

function TopPackagesCard({ packages }) {
  const { t } = useLanguage()
  if (!packages || packages.length === 0) return <Card title={t('sp_top_packages', 'Top packages')}><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('sp_no_bookings_yet_period', 'No bookings yet.')}</div></Card>
  return (
    <Card title={t('sp_top_packages_by_gmv', 'Top packages by GMV')}>
      {packages.map((p, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i === packages.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
          <div style={{ flex: 1, color: '#0f172a' }}>{p.PackageName || `Package #${p.Package_ID}`}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginRight: 12 }}>{p.bookings} {p.bookings === 1 ? t('sp_booking_singular', 'booking') : t('sp_booking_plural', 'bookings')}</div>
          <div style={{ fontWeight: 700, color: '#00d19f' }}>${Number(p.gmv || 0).toFixed(2)}</div>
        </div>
      ))}
    </Card>
  )
}

function AffiliateCard({ affiliate }) {
  const { t } = useLanguage()
  return (
    <Card title={t('sp_affiliate_utm_contribution', 'Affiliate / UTM contribution')}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <Tile label={t('sp_kpi_attributed_bookings', 'Attributed bookings')} value={Number(affiliate.attributedBookings) || 0} />
        <Tile label={t('sp_kpi_attributed_gmv', 'Attributed GMV')} value={'$' + Number(affiliate.attributedGmv || 0).toFixed(2)} />
        <Tile label={t('sp_kpi_distinct_affiliate_codes', 'Distinct affiliate codes')} value={Number(affiliate.distinctCodes) || 0} />
      </div>
    </Card>
  )
}

function HotelRefundCard({ hotel, refunds, bookingKpis }) {
  const { t } = useLanguage()
  const totalActive = Number(bookingKpis?.activeBookings) || 0
  const refundedCount = Number(refunds?.refundedCount) || 0
  const refundRate = totalActive > 0 ? ((refundedCount / (totalActive + refundedCount)) * 100).toFixed(1) : '0.0'
  return (
    <Card title={t('sp_operations', 'Operations')}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <Tile label={t('sp_kpi_hotel_bookings', 'Hotel bookings')} value={Number(hotel?.bookingsWithHotel) || 0} />
        <Tile label={t('sp_kpi_hotel_confirmed', 'Hotel confirmed')} value={Number(hotel?.confirmed) || 0} color="#16a34a" />
        <Tile label={t('sp_kpi_hotel_pending', 'Hotel pending')} value={Number(hotel?.pending) || 0} color="#92400e" />
        <Tile label={t('sp_kpi_refund_rate', 'Refund rate')} value={refundRate + '%'} color={Number(refundRate) > 5 ? '#dc2626' : '#0f172a'} />
      </div>
    </Card>
  )
}

function Tile({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || 'var(--secondary-950)', marginTop: 2 }}>{value}</div>
    </div>
  )
}
